import {
  ArrowDownIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  BellIcon,
  BookOpenIcon,
  BotIcon,
  BoxIcon,
  BrainIcon,
  CheckIcon,
  ChevronsUpDownIcon,
  CircleHelpIcon,
  CodeXmlIcon,
  CopyIcon,
  DownloadIcon,
  ExternalLinkIcon,
  FileTextIcon,
  HistoryIcon,
  InfoIcon,
  LightbulbIcon,
  Loader2Icon,
  MessageSquareIcon,
  MicIcon,
  MoonIcon,
  MoreHorizontalIcon,
  PackageIcon,
  PanelRightIcon,
  PencilIcon,
  PlusIcon,
  RefreshCwIcon,
  SettingsIcon,
  SparklesIcon,
  SquareIcon,
  SquareTerminalIcon,
  SunIcon,
  Trash2Icon,
  UserIcon,
  WrenchIcon,
  XIcon,
} from 'lucide-react';
import { type ComponentType, type ReactNode, useState } from 'react';

import {
  AccountAddIcon,
  AddIcon,
  AiIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CollapsedIcon,
  GroupIcon,
  LayoutIcon,
  LogoIcon,
  NotificationIcon,
  RefreshIcon,
  SearchIcon as CustomSearchIcon,
  SpaceIcon,
  ThemeIcon,
  UserGroupIcon,
  WorkspaceIcon,
} from '../../assets/icons';
import {
  CreateAiChatIcon,
  CreateAppIcon,
  CreateBotIcon,
  CreateChatIcon,
  CreateContactIcon,
  CreateGroupIcon,
  CreateWorkspaceIcon,
} from '../../assets/icons/create-glyphs';
import { useUiClientConfig } from '../../config-context';
import { cn } from '../../lib/utils';
import { FIGMA_RAW_ICONS, type RawFigmaIcon } from './icons/generated-icons';

/**
 * Icon descriptor consumed by `<Icon>` (架构方案 §5.7 / 见 16). Kept structurally
 * identical to `@hibro/shared`'s `IconSpec` but declared locally so this package
 * stays zero `@hibro/*` dependency; the renderer passes the shared value and it
 * matches by shape. `builtin` looks up `ICON_REGISTRY`; `resource` is an absolute
 * (`hibro-assets://`) image url with an optional dark variant.
 */
export type IconSpec =
  | { type: 'builtin'; id: string }
  | { type: 'resource'; light: string; dark?: string; mono?: boolean };

/** A registry entry: anything renderable from `size`/`className` (lucide fits). */
export type IconComponent = ComponentType<{
  size?: number;
  className?: string;
}>;

/**
 * The platform's original lucide-backed entries: semantic name -> component
 * (架构方案 §5.7 / 见 16). Kept as-is for backward compatibility; the Figma
 * design-system icons are added alongside under the `i-` prefix (see below),
 * so these bare names never collide with an imported icon. Lucide icons paint
 * with `currentColor`, inheriting the surrounding token color (见 13).
 */
const LUCIDE_ICON_REGISTRY: Record<string, IconComponent> = {
  settings: SettingsIcon,
  box: BoxIcon,
  sample: PackageIcon,
  account: UserIcon,
  userGroup: UserGroupIcon,
  workspace: WorkspaceIcon,
  space: SpaceIcon,
  accountAdd: AccountAddIcon,
  chat: MessageSquareIcon,
  bell: BellIcon,
  plus: PlusIcon,
  panel: PanelRightIcon,
  'chevron-left': ChevronLeftIcon,
  'chevron-right': ChevronRightIcon,
  collapsed: CollapsedIcon,
  sun: SunIcon,
  moon: MoonIcon,
  sparkles: SparklesIcon,
  more: MoreHorizontalIcon,
  close: XIcon,
  // Chat / AI panel glyphs (见 16): shared so business modules paint no lucide
  // of their own — they consume these via <Icon spec={{ type:'builtin', id }} />.
  copy: CopyIcon,
  check: CheckIcon,
  'refresh-cw': RefreshCwIcon,
  'arrow-up': ArrowUpIcon,
  'arrow-down': ArrowDownIcon,
  stop: SquareIcon,
  'chevrons-up-down': ChevronsUpDownIcon,
  'arrow-right': ArrowRightIcon,
  brain: BrainIcon,
  bot: BotIcon,
  'info-circle': InfoIcon,
  loader: Loader2Icon,
  wrench: WrenchIcon,
  history: HistoryIcon,
  trash: Trash2Icon,
  pencil: PencilIcon,
  download: DownloadIcon,
  'external-link': ExternalLinkIcon,
  mic: MicIcon,
  code: CodeXmlIcon,
  terminal: SquareTerminalIcon,
  'file-text': FileTextIcon,
  'book-open': BookOpenIcon,
  lightbulb: LightbulbIcon,
  // Platform-owned custom glyphs (见 16). `search` overrides lucide's default so
  // the whole app shares one search mark; `ai` is a multi-color brand icon.
  search: CustomSearchIcon,
  ai: AiIcon,
  // Duotone create glyphs: body strokes follow currentColor, accent strokes
  // use --app-color-accent. Commands name these from their manifest icon.
  'create-workspace': CreateWorkspaceIcon,
  'create-ai-chat': CreateAiChatIcon,
  'create-chat': CreateChatIcon,
  'create-group': CreateGroupIcon,
  'create-contact': CreateContactIcon,
  'create-bot': CreateBotIcon,
  'create-app': CreateAppIcon,
  layout: LayoutIcon,
  refresh: RefreshIcon,
  theme: ThemeIcon,
  notification: NotificationIcon,
  add: AddIcon,
  logo: LogoIcon,
  group: GroupIcon,
};

/**
 * Prefix for every icon imported from the Figma "Component Library" (见 16), so
 * the whole design set lives in one namespace and can't clash with the legacy
 * lucide names above. Outline (空心) variants use the base id (`i-search`);
 * filled (实心) variants carry a `-filled` suffix (`i-search-filled`).
 */
const FIGMA_ICON_PREFIX = 'i-';

/**
 * Wrap generated icon data into an `IconComponent`. The body is trusted,
 * build-time SVG markup produced by `scripts/generate-icons.mjs` (no user input),
 * injected via `dangerouslySetInnerHTML` to preserve raw SVG features that JSX
 * would mangle (`<mask>`/`<filter>`/gradients, kebab-case attributes). Near-black
 * paints are already normalized to `currentColor`, so monochrome icons follow the
 * foreground token; brand/multicolor glyphs keep their own hues.
 */
function createFigmaIcon(raw: RawFigmaIcon): IconComponent {
  function FigmaIcon({
    size = 20,
    className,
  }: {
    size?: number;
    className?: string;
  }): ReactNode {
    return (
      <svg
        width={size}
        height={size}
        viewBox={raw.vb}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
        className={cn('shrink-0', className)}
        dangerouslySetInnerHTML={{ __html: raw.body }}
      />
    );
  }
  return FigmaIcon;
}

const FIGMA_ICON_REGISTRY: Record<string, IconComponent> = Object.fromEntries(
  Object.entries(FIGMA_RAW_ICONS).map(([id, raw]) => [
    `${FIGMA_ICON_PREFIX}${id}`,
    createFigmaIcon(raw),
  ]),
);

/**
 * The platform's static, synchronous icon registry: semantic name -> component
 * (架构方案 §5.7 / 见 16). Merges the legacy lucide names with the full Figma
 * design set (`i-` prefixed). Resolved synchronously at import; consumers look
 * up by id via `<Icon spec={{ type: 'builtin', id }} />` with zero changes.
 */
export const ICON_REGISTRY: Record<string, IconComponent> = {
  ...LUCIDE_ICON_REGISTRY,
  ...FIGMA_ICON_REGISTRY,
};

interface IconProps {
  spec: IconSpec;
  /** Square pixel size; defaults to 20. */
  size?: number;
  className?: string;
  /**
   * Text whose first letter is drawn when a `builtin` id is missing from the
   * registry (graceful fallback, matching the legacy first-letter launcher). When
   * omitted a neutral placeholder icon is shown instead.
   */
  fallbackText?: string;
}

/**
 * A module `resource` icon (架构方案 §5.7 / 见 16). Picks the active theme's
 * variant (from `useUiClientConfig`) so the browser fetches a single asset — a
 * CSS `display:none` sibling would still be downloaded — and re-renders on theme
 * change because the config context updates.
 *
 * `mono` opts into CSS-mask tinting: the shape is drawn as a `currentColor` fill
 * through `mask-image`, so it follows the ambient text color (selection / hover)
 * exactly like a `builtin` icon — at the cost of flattening any multi-color art
 * to a single color. Without `mono` the image renders as-is via `<img>`.
 */
function ResourceIcon({
  light,
  dark,
  mono,
  size,
  className,
  fallbackText,
}: {
  light: string;
  dark?: string;
  mono?: boolean;
  size: number;
  className?: string;
  fallbackText?: string;
}): ReactNode {
  const { themeMode } = useUiClientConfig();
  const src = dark !== undefined && themeMode === 'dark' ? dark : light;
  const [failed, setFailed] = useState(false);
  const letter = fallbackText?.trim().charAt(0).toUpperCase();

  if (failed) {
    if (letter !== undefined && letter !== '') {
      return (
        <span
          className={cn(
            'inline-flex shrink-0 items-center justify-center font-semibold',
            className,
          )}
          style={{
            width: size,
            height: size,
            fontSize: Math.round(size * 0.7),
          }}
        >
          {letter}
        </span>
      );
    }
    return <CircleHelpIcon size={size} className={className} />;
  }

  if (mono === true) {
    return (
      <span
        aria-hidden="true"
        className={cn('inline-block shrink-0', className)}
        style={{
          width: size,
          height: size,
          backgroundColor: 'currentColor',
          WebkitMaskImage: `url("${src}")`,
          maskImage: `url("${src}")`,
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskPosition: 'center',
          WebkitMaskSize: 'contain',
          maskSize: 'contain',
        }}
      />
    );
  }
  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      className={cn('shrink-0', className)}
      onError={() => setFailed(true)}
    />
  );
}

/**
 * Render an `IconSpec` (架构方案 §5.7 / 见 16). `builtin` resolves synchronously
 * against `ICON_REGISTRY` (no async registration); `resource` renders the
 * module-shipped image, picking the active theme's variant (only that one is
 * fetched, 见 16) when a dark variant is present.
 */
function Icon({
  spec,
  size = 20,
  className,
  fallbackText,
}: IconProps): ReactNode {
  if (spec.type === 'resource') {
    return (
      <ResourceIcon
        light={spec.light}
        dark={spec.dark}
        mono={spec.mono}
        size={size}
        className={className}
        fallbackText={fallbackText}
      />
    );
  }

  const Resolved = ICON_REGISTRY[spec.id];
  if (Resolved !== undefined) {
    return (
      <Resolved size={size} className={cn(`size-${size / 4}`, className)} />
    );
  }

  const letter = fallbackText?.trim().charAt(0).toUpperCase();
  if (letter !== undefined && letter !== '') {
    return (
      <span
        className={cn(
          'inline-flex shrink-0 items-center justify-center font-semibold',
          className,
        )}
        style={{ width: size, height: size, fontSize: Math.round(size * 0.7) }}
      >
        {letter}
      </span>
    );
  }
  return <CircleHelpIcon size={size} className={className} />;
}

export { Icon };
